import React, {
  ComponentPropsWithoutRef,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { Table as AntTable } from 'antd';
import {
  Fields,
  SortDirection,
  CSSPixelsNumber,
} from '../../../infrastructure/types/common/common-types';
import { ColumnsType } from 'antd/es/table';
import {
  FilterDropdownProps,
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import { fadeAnimation, fadeAnimationCss, scrollbar } from '../../styles/GlobalStyle';
import locale from 'antd/es/locale/ru_RU';
import { Skeleton } from './Skeleton';
import UiKit from '../index';
import { EmptyData } from '../../components/EmptyData';

interface CustomStyles {
  cellPadding?: CSSSize;
  tableMaxWidth?: CSSSize | 'none';
}

interface TableProps<TableItem extends object, ColumnID extends string | undefined> {
  columns: Column<TableItem, ColumnID>[];
  data: TableItem[];
  renderCustomHeader?: () => ReactElement;
  showCustomHeader?: boolean;
  sort?: TableSort<ColumnID>;
  onResetSort?: () => void;
  mockDataSize?: number;
  className?: string;
  rowKey?: ((record: unknown, index: number) => string) | Fields<TableItem>;
  showTotal?: boolean;
  tableLayout?: 'auto' | 'fixed';
  totalPrefix?: PlainText;
  renderTotal?: (total: number) => ReactElement;
  loading?: boolean;
  // onRowClick - rowSettings
  scroll?: ScrollConf;
  emptyTableText?: PlainText;
  skeletonHeight?: CSSSize;
  rowClassName?: (record: TableItem, index: number) => string;
  testIds?: { main?: string; body?: string; row?: string };
  size?: 'small' | 'middle' | 'large';
  customStyles?: CustomStyles;
}

interface ScrollConf {
  height?: CSSPixelsNumber;
  width?: CSSPixelsNumber;
}

// TODO maybe add separated pagination component
interface Pagination {
  pageNumber: number;
  pageSize: number;
  total: number;
  hide?: boolean;
  hideOnSinglePage?: boolean;
  pageSizeOptions?: number[];
  disabled?: boolean;
  onChange: (page: PageNumber, pageSize: PageLimit) => void;
  onSizeChange?: (size: number) => void; // TODO add type PageSize
}

// TODO why here za*bal?
interface FilterDropdownRenderProps {
  confirm: (closeDropdown: boolean) => void;
  close: () => void;
}

interface SearchFilterDropdownProps {
  appliedSearch: PlainText;
  onConfirm: (searchString: PlainText) => void;
  onCancel: () => void;
  confirmTitle: string;
  cancelTitle: string;
  visible: boolean;
}

interface FilterDropDownConf {
  render?: (props: FilterDropdownRenderProps) => ReactElement;
  icon?: ReactNode;
  toggleVisible?: (visible: boolean) => void;
  visible?: boolean;
}

interface TableSort<ColumnID extends string | undefined> {
  columnID: ColumnID;
  direction: SortDirection;
}

type CustomColumnRenderer<TableItem> = (item: TableItem, value?: string) => React.ReactNode;
type CustomHeaderRenderer = () => React.ReactNode;
type CustomHeaderTooltip = () => React.ReactNode;
type AccessorColumnRenderer<TableItem> = Fields<TableItem>;
type SkeletonShape = 'circle' | 'square';

type Column<TableItem, ColumnID> =
  | ColumnNonSortable<TableItem>
  | ColumnSortable<TableItem, ColumnID>;

type ParentOrChildItem<ParentItem, ChildItem> =
  | ParentItem
  | (ChildItem & { children?: never; isParent?: never });

type AlignType = 'left' | 'right' | 'center';

// TODO: add key?
// TODO maybe remove accessorRenderer and leave only custom
interface ColumnBase<TableItem> {
  header: CustomHeaderRenderer | string;
  render: CustomColumnRenderer<TableItem> | AccessorColumnRenderer<TableItem>;
  filterDropdownConf?: FilterDropDownConf;
  sortable: boolean;
  filtered?: boolean;
  headerTooltip?: PlainText | CustomHeaderTooltip;
  headerTooltipWidth?: string;
  key?: string;
  width?: string | number; // TODO change type to template with px
  minWidth?: number;
  align?: AlignType;
  ellipsis?: boolean;
  hidden?: boolean;
  fixed?: 'left' | 'right';
  testId?: string;
  headerTestId?: string;
}

interface ColumnNonSortable<TableItem> extends ColumnBase<TableItem> {
  columnID?: never;
  onSort?: never;
  skeletonProps?: {
    shape: SkeletonShape;
  };
}

// columnID is required if onSort is present
interface ColumnSortable<TableItem, ColumnID> extends ColumnBase<TableItem> {
  columnID: ColumnID;
  onSort?: (direction: SortDirection) => void;
  skeletonProps?: {
    shape: SkeletonShape;
  };
}

const sortIconClassName = 'custom-sort-icon';

//Используем свой рендер шапки колонки таблицы потому что при отображении
//кастомного тултипа сортировка происходит по клику на ссылку в тултипе
function HeaderCell(props: ComponentPropsWithoutRef<'th'>) {
  return (
    <th
      {...props}
      onClick={e => {
        e.stopPropagation();
        const currentTarget = e.currentTarget as HTMLElement;
        const sortIconNode = currentTarget.querySelector(`.${sortIconClassName}`);
        if (sortIconNode?.contains(e.target as HTMLElement)) {
          props.onClick?.(e);
        }
      }}
    />
  );
}

const StyledTbody = styled.tbody``;

const getComponents = (bodyTestId?: string, rowTestId?: string) => ({
  header: {
    cell: HeaderCell,
  },
  ...(bodyTestId
    ? {
        body: {
          wrapper: (props: ComponentPropsWithoutRef<'tbody'>) => (
            <StyledTbody {...props} data-testid={bodyTestId} />
          ),
        },
      }
    : {}),
});

const DEFAULT_SKELETON_ROW = 10;

function Table<TableItem extends object, ColumnID extends string | undefined>({
  data,
  columns,
  sort,
  renderCustomHeader,
  showCustomHeader,
  className,
  mockDataSize,
  rowKey,
  tableLayout = 'fixed',
  onResetSort,
  showTotal,
  renderTotal,
  totalPrefix,
  scroll,
  emptyTableText,
  loading,
  skeletonHeight,
  rowClassName,
  testIds,
  size,
  customStyles,
}: TableProps<TableItem, ColumnID>): ReactElement {
  const antColumns: ColumnsType<TableItem> = columns
    .filter(column => !column.hidden)
    .map(column => {
      let antRender;
      let antAccessor;
      let headerAntRender;
      let headerTooltipAntRender;

      if (loading) {
        antRender = () => (
          <StyledSkeleton
            className={className}
            height={skeletonHeight || '40px'}
            shape={column.skeletonProps?.shape}
          />
        );
      } else if (typeof column.render === 'function') {
        antRender = (value: unknown, record: TableItem) =>
          typeof column.render === 'function' && column.render(record, value as string);
      } else {
        antAccessor = typeof column.render === 'string' ? column.render : undefined;
      }

      if (typeof column.header === 'function') {
        headerAntRender = () => typeof column.header === 'function' && column.header();
      } else {
        headerAntRender =
          typeof column.header === 'string' ? (
            <Header title={''}>{column.header}</Header>
          ) : undefined;
      }

      let sortOrder;

      if (sort && column.columnID === sort.columnID) {
        sortOrder = sort.direction;
      }

      if (column.headerTooltip) {
        if (typeof column.headerTooltip === 'function') {
          headerTooltipAntRender = {
            title: (column.headerTooltip as CustomHeaderTooltip)(),
            overlayInnerStyle: { whiteSpace: 'pre-line' as const },
          };
        } else {
          headerTooltipAntRender = {
            title: column.headerTooltip,
            overlayInnerStyle: { whiteSpace: 'pre-line' },
          };
        }
      }

      const antFilterDropDown = (props: FilterDropdownProps): ReactNode => {
        if (column.filterDropdownConf && column.filterDropdownConf.render) {
          const confirmFunc = (closeDropdown: boolean) => {
            // eslint-disable-next-line react/prop-types
            props.confirm({ closeDropdown });
          };

          const closeFunc = () => {
            // eslint-disable-next-line react/prop-types
            props.close();
          };

          return column.filterDropdownConf.render({ confirm: confirmFunc, close: closeFunc });
        }
      };

      return {
        title: headerAntRender,
        dataIndex: antAccessor,
        render: antRender,
        sortOrder,
        filtered: column.filtered,
        filteredValue: [],
        key: column.key || undefined,
        sorter: column.sortable,
        onSort: column.onSort,
        columnID: column.columnID,
        align: column.align || 'left',
        ellipsis: column.ellipsis !== undefined ? column.ellipsis : true,
        fixed: column.fixed,
        width: column.width,
        minWidth: column.minWidth,
        showSorterTooltip: {
          ...headerTooltipAntRender,
          className: sortIconClassName,
          ...(column.headerTooltipWidth && {
            overlayStyle: { maxWidth: column.headerTooltipWidth },
          }),
        },
        filterDropdown: column.filterDropdownConf ? antFilterDropDown : undefined,
        filterIcon: column.filterDropdownConf ? column.filterDropdownConf.icon : undefined,
        filterDropdownProps: {
          open: column.filterDropdownConf ? column.filterDropdownConf.visible : undefined,
          onOpenChange: column.filterDropdownConf
            ? column.filterDropdownConf.toggleVisible
            : undefined,
        },
        onCell: column.testId
          ? () => ({ 'data-testid': column.testId } as React.TdHTMLAttributes<HTMLTableCellElement>)
          : undefined,
        onHeaderCell: column.headerTestId
          ? () =>
              ({
                'data-testid': column.headerTestId,
              } as React.ThHTMLAttributes<HTMLTableCellElement>)
          : undefined,
      };
    });

  const antOnChange: <RecordType>(
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<RecordType> | SorterResult<RecordType>[],
    extra: TableCurrentDataSource<RecordType>,
  ) => void = (pagination, filters, sorter, extra) => {
    const sorterResult: SorterResult<never> = sorter as SorterResult<never>;

    if (extra.action === 'sort') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (sorterResult.column && sorterResult.order && sorterResult.column.onSort) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        sorterResult.column.onSort(sorterResult.order);
      } else {
        onResetSort && onResetSort();
      }
    }
  };

  const getMockData = ({ size = DEFAULT_SKELETON_ROW }): TableItem[] => {
    return Array.from({ length: size }, (v, i) => {
      return { id: i } as TableItem;
    });
  };

  // TODO fix types

  return (
    <TableCustomStylesWrapper
      data-scroll-target={true}
      $cellPadding={customStyles?.cellPadding}
      $tableMaxWidth={customStyles?.tableMaxWidth}
    >
      {showCustomHeader && (
        <CustomHeader>{renderCustomHeader && renderCustomHeader()}</CustomHeader>
      )}
      <StyledTable
        className={className}
        pagination={false}
        components={getComponents(testIds?.body)}
        size={size}
        scroll={scroll ? { x: scroll.width, y: scroll.height } : undefined}
        showHeader={!showCustomHeader}
        locale={{
          ...locale.Table,
          emptyText: <EmptyData text={emptyTableText || ('Нет данных' as PlainText)}></EmptyData>,
        }}
        tableLayout={tableLayout}
        dataSource={loading ? getMockData({ size: mockDataSize }) : data}
        columns={antColumns}
        onChange={antOnChange}
        sortDirections={['ascend', 'descend']}
        rowKey={rowKey ? (rowKey as string) : undefined}
        rowClassName={rowClassName}
        data-testid={testIds?.main}
        onRow={
          testIds?.row
            ? () => ({ 'data-testid': testIds.row } as React.HTMLAttributes<HTMLTableRowElement>)
            : undefined
        }
      />
    </TableCustomStylesWrapper>
  );
}

// TODO rename
function FilterByText({
  appliedSearch,
  onConfirm,
  onCancel,
  cancelTitle,
  confirmTitle,
  visible,
}: SearchFilterDropdownProps): ReactElement {
  const [search, setSearch] = useState<PlainText>('' as PlainText);

  useEffect(() => {
    setSearch(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    if (!visible) {
      setSearch(appliedSearch);
    }
  }, [visible]);

  return (
    <DropdownWrapper>
      <DropdownInput>
        <UiKit.Input
          size={'middle'}
          value={search}
          onChange={value => {
            setSearch(value);
          }}
          onPressEnter={() => onConfirm(search)}
        />
      </DropdownInput>
      <DropdownControls>
        <UiKit.Button
          size={'small'}
          type={'text'}
          onClick={() => {
            onCancel();
          }}
        >
          {cancelTitle}
        </UiKit.Button>

        <UiKit.Button
          size={'small'}
          type={'primary'}
          icon={<UiKit.icons.Search />}
          onClick={() => {
            onConfirm(search);
          }}
        >
          {confirmTitle}
        </UiKit.Button>
      </DropdownControls>
    </DropdownWrapper>
  );
}

const DropdownWrapper = styled.div`
  min-width: 220px;
  padding: 12px;
`;

const DropdownControls = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const DropdownInput = styled.div``;

const CustomHeader = styled.div`
  background: ${colorsPalette.lightGray100};
  border-radius: 8px 8px 0 0;
  box-sizing: border-box;
  height: 55px;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  ${fadeAnimationCss}
`;

const StyledSkeleton = styled(Skeleton)`
  &.ant-skeleton.ant-skeleton-element {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
  }

  &.ant-skeleton.ant-skeleton-element .ant-skeleton-input {
    width: 100%;
    min-width: 0;
  }
`;

const TableCustomStylesWrapper = styled.div<{
  $cellPadding?: CSSSize;
  $tableMaxWidth?: CSSSize | 'none';
}>`
  ${props =>
    props.$tableMaxWidth &&
    `
    && .ant-table-wrapper table {
      max-width: ${props.$tableMaxWidth} !important;
    }
  `}

  ${props =>
    props.$cellPadding &&
    `
    .ant-table-wrapper .ant-table-cell,
    .ant-table-wrapper .ant-table-thead > tr > th,
    .ant-table-wrapper .ant-table-tbody > tr > td {
      padding: ${props.$cellPadding};
    }
  `}
`;

const StyledTable = styled(AntTable)`
  & {
    .ant-table-body {
      ${scrollbar};
    }
  }

  .ant-table-tbody > tr {
    animation: ${fadeAnimation} 600ms ease-out;
  }
  .ant-pagination > .ant-pagination-item-active {
    border-color: ${colorsPalette.dustyBlue500};
  }

  .ant-pagination .ant-pagination-item-active a {
    color: ${colorsPalette.dustyBlue500};
  }

  &.ant-table-wrapper {
    .ant-table {
      scrollbar-color: initial;
    }
    .ant-table-thead .ant-table-cell .ant-table-column-title {
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
    table {
      max-width: 950px !important;

      thead {
        animation: ${fadeAnimation} 600ms ease-out;
      }
    }
  }

  &.ant-table-wrapper .ant-table-column-sorter-up.active,
  &.ant-table-wrapper .ant-table-column-sorter-down.active {
    color: ${colorsPalette.dustyBlue500};
  }

  &.ant-table-wrapper .ant-table-filter-trigger.active {
    color: ${colorsPalette.dustyBlue500};
  }

  .ant-table-tbody > tr.table__row-highlighted > td {
    background-color: ${colorsPalette.lightBlue100};
  }
` as typeof AntTable;
const Header = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

Table.FilterByText = FilterByText;

export {
  Table,
  type TableSort,
  type Column,
  type Pagination,
  type FilterDropdownRenderProps,
  type ParentOrChildItem,
};
